import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const CartContext = createContext(null);

export function useCart() {
  return useContext(CartContext);
}

function loadLocalCart() {
  try {
    const stored = localStorage.getItem("ogxbox_cart");
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(loadLocalCart);
  const prevUserId = useRef(null);
  const saveTimeout = useRef(null);

  // When user changes (login/logout/switch), load their cart from server
  useEffect(() => {
    const userId = user?._id || user?.id || null;

    // Skip if same user
    if (userId === prevUserId.current) return;
    prevUserId.current = userId;

    if (!userId) {
      // Logged out — clear cart
      setItems([]);
      localStorage.removeItem("ogxbox_cart");
      return;
    }

    // Logged in — fetch their cart from server
    const fetchCart = async () => {
      try {
        const { data } = await axios.get(`${API}/api/cart`);
        if (Array.isArray(data) && data.length > 0) {
          setItems(data);
          localStorage.setItem("ogxbox_cart", JSON.stringify(data));
        } else {
          // If server cart is empty but local has items (added before login), save local to server
          const local = loadLocalCart();
          if (local.length > 0) {
            setItems(local);
            try { await axios.put(`${API}/api/cart`, local); } catch {}
          } else {
            setItems([]);
            localStorage.removeItem("ogxbox_cart");
          }
        }
      } catch {
        // API failed — use local cart
      }
    };
    fetchCart();
  }, [user]);

  // Save to localStorage + debounced save to server on every change
  useEffect(() => {
    localStorage.setItem("ogxbox_cart", JSON.stringify(items));

    // Debounce server save (500ms)
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    if (user) {
      saveTimeout.current = setTimeout(() => {
        axios.put(`${API}/api/cart`, items).catch(() => {});
      }, 500);
    }
  }, [items, user]);

  const addItem = useCallback((product, quantity = 1, selectedColor = null) => {
    // $20 upcharge for custom color on consoles/bundles
    const colorUpcharge = (selectedColor && ["consoles", "bundles"].includes(product.category)) ? 20 : 0;
    const itemPrice = product.price + colorUpcharge;
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        const newQty = Math.min(existing.quantity + quantity, product.stock || 999);
        return prev.map(i => i.product_id === product.id ? { ...i, quantity: newQty, selected_color: selectedColor || i.selected_color, price: selectedColor ? itemPrice : i.price } : i);
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: itemPrice,
        image_url: product.image_url,
        quantity: Math.min(quantity, product.stock || 999),
        selected_color: selectedColor
      }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.product_id !== productId));
      return;
    }
    setItems(prev => prev.map(i => i.product_id === productId ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    if (user) {
      axios.put(`${API}/api/cart`, []).catch(() => {});
    }
  }, [user]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const value = useMemo(() => ({
    items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice
  }), [items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
